import React from "react";
import { IRealTimeAutoResult, IVehicleResult, ICoverageResult } from "../classes/Domain";
import * as _ from 'lodash';
import Utilities from "../classes/Utilities";

interface IProps {
    currentRate: IRealTimeAutoResult | null | undefined;
    /** Req #9: same blue alternate-quote box as on the payment-plans slide. */
    alternateCoverageBanner?: React.ReactNode;
}

interface IState {
    vehiclesData: Array<{
        vehicleRate: IVehicleResult;
        coverageCodes: Array<string>;
        fees: Array<ICoverageResult>;
        hasPricedComp: boolean;
        hasPricedColl: boolean;
    }>;
}

/** Rate results carry COMP/COLL either as codes (COMP/COLL) or as titles (Comprehensive/Collision). */
function isCompCoverage(c: ICoverageResult): boolean {
    const code = (c.code || "").toUpperCase();
    const title = (c.title || "").toUpperCase();
    return code === "COMP" || code === "COMPREHENSIVE" || title.indexOf("COMPREHENSIVE") >= 0;
}

function isCollCoverage(c: ICoverageResult): boolean {
    const code = (c.code || "").toUpperCase();
    const title = (c.title || "").toUpperCase();
    return code === "COLL" || code === "COLLISION" || title.indexOf("COLLISION") >= 0;
}

class CoveragesOverview extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            vehiclesData: []
        };
    }

    componentDidMount() {
        this.updateAllVehiclesData();
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.currentRate !== this.props.currentRate) {
            this.updateAllVehiclesData();
        }
    }

    updateAllVehiclesData() {
        const { currentRate } = this.props;

        const validVehicles = currentRate?.vehicleRates?.filter(vr => !!vr.make) || [];

        if (validVehicles.length === 0) {
            this.setState({ vehiclesData: [] });
            return;
        }

        const vehiclesData = validVehicles.map(vehicleRate => {
            const ratedLiability = (vehicleRate.liabilityCoverages || []).filter(c => c && c.amt);
            const ratedVehicle = (vehicleRate.vehicleCoverages || []).filter(c => c && c.amt);
            const covs = _.union(
                _.map(ratedLiability, "code"),
                _.map(ratedVehicle, "code")
            );

            const ratedAll = [...ratedLiability, ...ratedVehicle];
            return {
                vehicleRate: vehicleRate,
                coverageCodes: covs,
                fees: vehicleRate.fees || [],
                hasPricedComp: ratedAll.some(isCompCoverage),
                hasPricedColl: ratedAll.some(isCollCoverage)
            };
        });

        this.setState({ vehiclesData });
    }

    getCoverage(vehicleResult: IVehicleResult, coverageCode: string) {
        let coverage = _.find(vehicleResult.liabilityCoverages, c => c.code === coverageCode);
        return coverage ? coverage : _.find(vehicleResult.vehicleCoverages, c => c.code === coverageCode);
    }

    /** Req #8: liability-only quotes explicitly show COMP/COLL as not selected. */
    renderNotSelectedCoverage(title: string) {
        return <div className="item">
            <label className="left-cell">
                {title}
            </label>
            <label className="right-cell">
                <span className="text-muted">(not selected)</span>
            </label>
        </div>
    }

    renderCoverage(vehicleRate: IVehicleResult, coverageCode: string) {
        let coverage = this.getCoverage(vehicleRate, coverageCode);
        return !coverage
            ? null
            : <div className="item">
                <label className="left-cell">
                    {coverage.title ? coverage.title : "No Coverage"}
                </label>
                <label className="right-cell">
                    <strong>{coverage.amt ? Utilities.formatCurrency(coverage.amt) : ""}</strong>
                </label>
            </div>
    }

    render() {
        const { vehiclesData } = this.state;

        if (vehiclesData.length === 0) {
            return (
                <div className="vehicle-coverages text-center p-4">
                    <p className="text-muted m-0">Please select a payment plan to view coverage details.</p>
                </div>
            );
        }

        return (
            <div className="all-vehicles-coverages-wrapper">
                {vehiclesData.map((vData, vIdx) => {
                    const { vehicleRate, coverageCodes, fees, hasPricedComp, hasPricedColl } = vData;
                    const vehicleKey = `${vehicleRate.year}-${vehicleRate.make}-${vehicleRate.model}-${vIdx}`;

                    return (
                        <div className="vehicle-coverages mb-5" key={vehicleKey}>
                            <div className="vehicle-name">
                                <strong className="vehicle-title">
                                    {`${vehicleRate.year} ${vehicleRate.make} ${vehicleRate.model}`}
                                </strong>
                            </div>

                            {coverageCodes.map((coverageCode) => (
                                <div className="coverage" key={`${vehicleKey}-${coverageCode}`}>
                                    <div className="coverage-name">
                                        <div>{coverageCode}</div>
                                    </div>
                                    {this.renderCoverage(vehicleRate, coverageCode)}
                                </div>
                            ))}

                            {!hasPricedComp && (
                                <div className="coverage" key={`${vehicleKey}-COMP-not-selected`}>
                                    <div className="coverage-name">
                                        <div>COMP</div>
                                    </div>
                                    {this.renderNotSelectedCoverage("Comprehensive")}
                                </div>
                            )}

                            {!hasPricedColl && (
                                <div className="coverage" key={`${vehicleKey}-COLL-not-selected`}>
                                    <div className="coverage-name">
                                        <div>COLL</div>
                                    </div>
                                    {this.renderNotSelectedCoverage("Collision")}
                                </div>
                            )}

                            {fees.map((fee, fIdx) => (
                                <div className="coverage" key={`${vehicleKey}-fee-${fIdx}`}>
                                    <div className="item">
                                        <label className="left-cell text-uppercase">
                                            {fee.title}
                                        </label>
                                        <label className="right-cell">
                                            <strong>{fee.amt ? Utilities.formatCurrency(fee.amt) : ""}</strong>
                                        </label>
                                    </div>
                                </div>
                            ))}

                            <div className="item total">
                                <label className="left-cell">Total for 6 month term</label>
                                <label className="right-cell">
                                    <strong>{Utilities.formatCurrency(vehicleRate.total)}</strong>
                                </label>
                            </div>
                        </div>
                    );
                })}
                {this.props.alternateCoverageBanner}
            </div>
        );
    }
}

export default CoveragesOverview;